<div class="!overflow-x-hidden">

  <!-- The graph -->
  <div #plotWrapper class="mb-4">
    <plotly-plot
      [data]="plotData"
      [config]="{ displaylogo: false }"
      [useResizeHandler]="false"
      appSafeResizeHandler
      [style]="{position: 'relative', width: '100%', height: '100%'}"
      [layout]="plotLayout" />
  </div>

  <!--
  Picker for features which should be controlled by the profile
  -->
  <div class="mb-2">
    <app-shaker-control-feature-selection
      [(selection)]="_pickedFeatures"
      [required]="true"
      [disabled]="readonly" />
  </div>

  <!--
  The profile-sequence table.

  Consists of two tables, one for initial values and headers
  and one for mutable part of the sequence.
  -->
  <div class="flex justify-center">
    <!--
    Adding a border here so the end of the table does not look
    like it's being cut off by the button row.
    -->
    <div class="
      min-w-[800px] max-w-[850px]
      border-0 border-b-[1px] border-gray-90 border-solid rounded-b
      "
      >

      <!-- Initial/starting values -->
      <mat-table
        [dataSource]="initialValues"
        class="border-b-0"
        >
        <ng-container matColumnDef="pre">
          <mat-header-cell *matHeaderCellDef></mat-header-cell>
          <mat-cell
            *matCellDef="let entry"
            >
            <!-- placeholder, as there is no need to re-order initial values -->
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="type">
          <mat-header-cell *matHeaderCellDef>Type</mat-header-cell>
          <mat-cell *matCellDef="let entry;">
            <span>{{ entry.typeDisplayName ?? "" }}</span>
          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="value">
          <mat-header-cell *matHeaderCellDef>Value</mat-header-cell>
          <mat-cell *matCellDef="let entry; let idx = index;">
            <app-shaker-profile-value-input
              class="block w-full"
              [type]="entry.type"
              [min]="entry.min"
              [max]="entry.max"
              [unit]="entry.unit"
              [step]="entry.step"
              [value]="entry.value"
              (valueChange)="_updateSequencePartValue(idx, $event, 'initial')"
              [disabled]="readonly" />

          </mat-cell>
        </ng-container>

        <ng-container matColumnDef="actions">
          <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
          <mat-cell *matCellDef="let element;" />
        </ng-container>

        <mat-header-row *matHeaderRowDef="columns" />
        <mat-row
          *matRowDef="let row; columns: columns;"
          class="!bg-gray-95" />

        <!--
        no-data row

        <tr> instead of <mat-row> because of bug, requiring to define CdkColumnDef
          -->
          <tr class="mat-row block" *matNoDataRow>
            <td class="mat-cell block !text-center text-orange-50">
              <div class="leading-[48px]">Select at least one feature</div>
            </td>
          </tr>
        </mat-table>


        <!--
        Mutable sequence of user inputs.
        -->
        <mat-table
          [dataSource]="dataSource$"
          cdkDropList
          [cdkDropListDisabled]="!draggingEnabled || readonly"
          (cdkDropListDropped)="_onSequencePartDrop($event)"
          id="sequence-table"
          >
          <ng-container matColumnDef="pre">
            <mat-header-cell *matHeaderCellDef></mat-header-cell>
            <mat-cell
              *matCellDef="let entry"
              (mousedown)="draggingEnabled = true"
              (mouseup)="draggingEnabled = false"
              cdkDragHandle
              class="cursor-grab"
              [class.cursor-grabbing]="draggingEnabled"
              >
              <ion-icon name="reorder-two-outline"></ion-icon>
            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="type">
            <mat-header-cell *matHeaderCellDef>Type</mat-header-cell>
            <mat-cell *matCellDef="let entry; let idx = index;">
              @if (selectedFeatures; as features) {
                <mat-select
                  placeholder="Select type..."
                  (selectionChange)="_valueTypeChanged(idx, $event.value)"
                  [value]="entry.type"
                  [disabled]="readonly"
                  >
                  @for (f of features; track f) {
                    <mat-option [value]="f.id">
                      {{ f.name }} ({{ f.unit }})
                    </mat-option>
                  }
                  <!--
                  Show only the name without the unit, because
                  after selection the unit is displayed in the
                  suffix of the value input (de-duplicate information).
                  -->
                  <mat-select-trigger>{{ entry.typeDisplayName ?? '&lt;Failed to acquire feature name&lt;' }}</mat-select-trigger>
                </mat-select>
              } @else {
                <ion-skeleton-text />
              }

            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="value">
            <mat-header-cell *matHeaderCellDef>Value</mat-header-cell>
            <mat-cell *matCellDef="let entry; let idx = index;">
              <app-shaker-profile-value-input
                class="block w-full"
                [type]="entry.type"
                [min]="entry.min"
                [max]="entry.max"
                [unit]="entry.unit"
                [step]="entry.step"
                [value]="entry.value"
                (valueChange)="_updateSequencePartValue(idx, $event, 'mutable')"
                [disabled]="readonly" />

            </mat-cell>
          </ng-container>

          <ng-container matColumnDef="actions">
            <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
            <mat-cell
              *matCellDef="let element; let idx = index"
              class="justify-center"
              >
              @if(element.error) {
                <div
                  matTooltip="Duplicate types within the same time frame are not allowed."
                  class="flex"
                  >
                  <ion-icon
                    name="warning-outline"
                    class="text-errorRed-50 flex-shrink-0" />
                </div>
              }
              <button
                mat-icon-button
                (click)="_copySequencePart(element)"
                [disabled]="readonly"
                >
                <ion-icon name="copy-outline" />
              </button>
              <button
                mat-icon-button
                (click)="_removeSequencePart(idx)"
                [disabled]="readonly"
                >
                <ion-icon name="trash-outline" />
              </button>
            </mat-cell>
          </ng-container>

          <!-- <mat-header-row *matHeaderRowDef="columns" /> -->
          <mat-row
            *matRowDef="let row; columns: columns; let odd = odd;"
            cdkDrag
          [ngClass]="{
            '!border-errorRed-50 !border !border-solid rounded': row.error,
            '!bg-indigo-95': row.type === 'TIME',
          }" />
        </mat-table>

      </div>
    </div>


    <!--
    Overlay, requesting a new profile name from users
    who want to save the entered profile as a new profile.
    -->
    <div
      #profileNameOverlay
    class="
      hidden
      absolute
      top-0 left-0
      z-10
      w-full h-full
      backdrop-blur-sm
      bg-gradient-to-br from-[#7b7b7b80] to-[#464646b3]
    "
      >
    <div class="
      absolute
      top-1/2 left-1/2
      -translate-x-1/2 -translate-y-1/2
      flex flex-row flex-wrap
      width-full min-w-[200px]
    ">
        <mat-form-field class="w-full">
          <mat-label class="text-white">Enter the new profile's name</mat-label>
          <input
            matInput
            type="text"
            [formControl]="newProfileNameControl"
            />
          @if (newProfileNameControl.invalid) {
            <mat-error>
              {{ profileNameErrorMessage$ | async }}
            </mat-error>
          }
        </mat-form-field>

        <div class="w-full flex justify-end gap-2">
          <button
            mat-raised-button
            color="accent"
            (click)="profileNameOverlayResult$.next('canceled')"
            >
            Abort
          </button>

          <button
            [disabled]="newProfileNameControl.invalid"
            mat-raised-button
            color="primary"
            (click)="profileNameOverlayResult$.next('accepted')"
            >
            Confirm
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Button row -->
  <ng-template #buttonRow>
    @if(readonly) {
      <button mat-stroked-button (click)="closeDialog()">Close</button>
    } @else {
      <button
        mat-stroked-button
        class="mr-auto"
        (click)="_addSequencePart()"
        [disabled]="selectedFeatures.length === 0"
        matTooltip="Add a new row to the sequence"
        >
        <ion-icon name="add-outline" />
        Add part
      </button>

      <button
        mat-stroked-button
        class="mr-auto"
        (click)="_addSelectedFeatureSequenceBlock()"
        [disabled]="selectedFeatures.length === 0"
        matTooltip="Add a row for each feature & one waiting time entry"
        >
        <ion-icon name="add-outline" />
        Add sequence
      </button>

      <button mat-stroked-button (click)="closeDialog()">Close</button>

      <app-split-button
        [disabled]="sequenceContainsErrors"
        matTooltip="The profile sequence contains errors (empty rows or duplicate setpoint types within the same time frame)."
        [matTooltipDisabled]="!sequenceContainsErrors"
        (click)="_submit()"
        [indicateRunningAction]="isSubmitting()"
      >
        <span buttonContent>Apply profile</span>
        <button
          mat-menu-item
          (click)="_submit('create')"
        >
          Apply &amp; save as new profile
        </button>
      </app-split-button>
    }
  </ng-template>
